<template>
	<div :class="useTransitions ? 'with-transitions' : 'without-transitions'" class="page-component-container">
		<transition :name="useTransitions ? componentTransition : null">
			<div :key="key" class="components-container components-full row" v-if="showResults">
				<div class="col-sm-12">
					<page-component
							:options="resultOptions"
							component="Results"
							side="full"
							v-on:goBack="onGoBack"
							v-on:pickChoice="onPickResultChoice"
					/>
				</div>
			</div>
			<div :key="key"
			     class="components-container components-side-by-side row" v-else-if="!forceFull && hasTwoComponents">
				<div class="col-sm-6">
					<page-component
							:component="leftComponent"
							:description="description"
							:options="leftComponentOptions"
							:title="title"
							side="left"
							v-on:continue="onContinue"
							v-on:end="onEnd"
							v-on:goBack="onGoBack"
							v-on:pickChoice="onPickResultChoice"
					/>
				</div>
				<div class="col-sm-6" v-if="hasTwoComponents">
					<page-component
							:component="rightComponent"
							:description="description"
							:options="rightComponentOptions"
							:title="title"
							side="right"
							v-on:continue="onContinue"
							v-on:end="onEnd"
							v-on:goBack="onGoBack"
							v-on:pickChoice="onPickResultChoice"
					/>
				</div>
			</div>
			<div :key="key" class="components-container components-full row" v-else>
				<div class="col-sm-12">
					<page-component
							:component="leftComponent"
							:description="description"
							:options="leftComponentOptions"
							:title="title"
							side="full"
							v-on:continue="onContinue"
							v-on:end="onEnd"
							v-on:goBack="onGoBack"
							v-on:pickChoice="onPickResultChoice"
					/>
				</div>
			</div>
		</transition>
	</div>
</template>
<script>
	import {mapGetters} from 'vuex'
	import PageComponent from './page-component'
	import PagePhase from '@/enum/PagePhase'
	import GamePhase from '@/enum/GamePhase'
	import AppConfig from '@/app-config'
	import Breadcrumb from '@/components/breadcrumb'
	import * as MutationTypes from '@/store/mutation-types'
	import GameType from '@/enum/GameType'
	import ResultType from '@/enum/ResultType'
	import { $eBus } from '@/event-bus';
	import GameClientEvent from '@/enum/GameClientEvent';

	export default {
		name: 'page-component-container',
		props: {
			breadcrumbUrl: {
				type: String,
				required: false
			},
			title: {
				type: String,
				required: false
			},
			description: {
				type: String,
				required: false
			},
			leftComponent: {
				type: String,
				required: true
			},
			leftComponentOptions: {
				type: Object,
				required: false,
				default() {
					return {}
				}
			},
			rightComponent: {
				type: String,
				required: false
			},
			rightComponentOptions: {
				type: Object,
				required: false,
				default() {
					return {}
				}
			},
			useTransitions: {
				type: Boolean,
				required: false,
				default: true
			}
		},
		components: {
			PageComponent,
			Breadcrumb
		},
		data() {
			return {
				componentTransition: AppConfig.componentTransition
			}
		},
		mounted() {
			this.$store.commit(MutationTypes.SET_BREADCRUMB_TO, this.breadcrumbUrl);
			$eBus.$on(GameClientEvent.RoomMessage, this.onRoomMessage);
		},
		destroyed() {
			$eBus.$off(GameClientEvent.RoomMessage, this.onRoomMessage);
		},
		computed: {
			...mapGetters({
				currentCase: 'getCurrentCase',
				actualCurrentPageIndex: 'getCurrentPageIndex',
				currentPagePhase: 'getCurrentPagePhase',
				pageHistory: 'getPageHistory',
				gameType: 'getGameType',
			}),

			forceFull() {
				return this.isSinglePlayer;
			},

			isSinglePlayer() {
				return this.gameType === GameType.SinglePlayer
			},

			hasTwoComponents() {
				return this.leftComponent && this.rightComponent
			},

			key() {
				// This allows the transition to figure out that it needs to create a new instance of the object and fade between them
				return this.actualCurrentPageIndex + '-' + this.currentPagePhase
			},

			useResult() {
				return !this.isSinglePlayer && (this.leftComponent === 'Choices' || this.rightComponent === 'Choices')
			},

			showResults() {
				return this.currentPagePhase === PagePhase.Result
			},

			resultOptions() {
				if (this.leftComponent === 'Choices') {
					return this.leftComponentOptions
				} else if (this.rightComponent === 'Choices') {
					return this.rightComponentOptions
				} else {
					throw new Error(
						`Could not find choice component on left side (${this.leftComponent}) or right side (${this.rightComponent})`)
				}
			},

			showBreadcrumb() {
				return this.breadcrumbUrl && this.currentCase
			},
		},
		methods: {
			onPickResultChoice(choice) {
				console.log('page-component-container.onPickResultChoice', choice);

				this.onContinue(choice && choice.hasOwnProperty('gotoPageIndex') ? choice.gotoPageIndex : null)
			},
			onGoBack() {
				if (this.actualCurrentPageIndex === 0) {
					return;
				}

				this.$gameClient.send({
					_currentPageIndex: this.actualCurrentPageIndex - 1
				});
			},
			onEnd() {
				this.$store.commit(MutationTypes.SET_NAVIGATION_GUARD_ENABLED, false)

				this.$gameClient.send({
					_phase: GamePhase.End
				})
			},
			onContinue(gotoPageIndex = null) {
				console.log('page-component-container.onContinue', gotoPageIndex);
				if (this.useResult && this.currentPagePhase !== PagePhase.Result) {
					this.$gameClient.send({
						_pagePhase: PagePhase.Result
					})
				} else {
					let currentPageIndex = this.actualCurrentPageIndex

					if (gotoPageIndex === null) {
						if (this.leftComponentOptions) {
							if (this.leftComponentOptions.hasOwnProperty('gotoPageIndex')) {
								gotoPageIndex = this.leftComponentOptions.gotoPageIndex
							} else if (this.leftComponentOptions.hasOwnProperty('gotoPageId')) {
								gotoPageIndex = this.leftComponentOptions.gotoPageId - 1
							}

							//Because of mistakes in database, this is forced
							if (this.leftComponentOptions.hasOwnProperty(
								'resultType') && this.leftComponentOptions.resultType === ResultType.NoAnswer) {
								gotoPageIndex = currentPageIndex + 1
							}
						}
					}

					if (gotoPageIndex === null || gotoPageIndex === currentPageIndex) {
						gotoPageIndex = currentPageIndex + 1
					}

					this.$gameClient.send({
						_pagePhase: PagePhase.Normal,
						_currentPageIndex: gotoPageIndex
					})
				}
			},
			onRoomMessage(room, message) {
				const parts = message.split(':');
				if (parts[0] == 'TimerEvent.Complete') {
					this.onContinue();
				}
			},
		}
	}
</script>

<style lang="less">
	.page-component-container.with-transitions {
		.components-container {
			position: absolute;
			left: 0;
			width: 100%;
		}
	}

	.components-container {
		margin-left: 0 !important;
		margin-bottom: 40px;
	}
</style>
